import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { fAPIDate } from 'src/utils/format-time';

import { IChequeItem } from 'src/types/cheque';
import {
  IDebtor,
  IFactorableSummary,
  IFactoringBatch,
  IFactoringCandidate,
} from 'src/types/cheque-factoring';

import axios from '../../utils/axios';

interface IPaginatedState<T> {
  status: 'idle' | 'loading' | 'success' | 'failure';
  error: string | undefined;
  count: number;
  page: number;
  pageSize: number;
  pageCount: number;
  items: T[];
}

interface IInitialState {
  candidates: IPaginatedState<IFactoringCandidate>;
  batches: IPaginatedState<IFactoringBatch>;
  batch: {
    status: 'idle' | 'loading' | 'success' | 'failure';
    error: string | undefined;
    data: IFactoringBatch | null;
  };
  overdue: IPaginatedState<IChequeItem>;
  debtors: IPaginatedState<IDebtor>;
  factorableSummary: {
    status: 'idle' | 'loading' | 'success' | 'failure';
    error: string | undefined;
    data: IFactorableSummary | null;
  };
}

const initialState: IInitialState = {
  candidates: {
    status: 'idle',
    error: undefined,
    count: 0,
    page: 1,
    pageSize: 10,
    pageCount: 0,
    items: [],
  },
  batches: {
    status: 'idle',
    error: undefined,
    count: 0,
    page: 1,
    pageSize: 10,
    pageCount: 0,
    items: [],
  },
  batch: {
    status: 'idle',
    error: undefined,
    data: null,
  },
  overdue: {
    status: 'idle',
    error: undefined,
    count: 0,
    page: 1,
    pageSize: 10,
    pageCount: 0,
    items: [],
  },
  debtors: {
    status: 'idle',
    error: undefined,
    count: 0,
    page: 1,
    pageSize: 10,
    pageCount: 0,
    items: [],
  },
  factorableSummary: {
    status: 'idle',
    error: undefined,
    data: null,
  },
};

const chequeFactoring = createSlice({
  name: 'chequeFactoring',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getFactoringCandidates.pending, (state) => {
        state.candidates.status = 'loading';
      })
      .addCase(getFactoringCandidates.fulfilled, (state, action) => {
        state.candidates.status = 'success';
        Object.assign(state.candidates, action.payload);
      })
      .addCase(getFactoringCandidates.rejected, (state, action) => {
        state.candidates.status = 'failure';
        state.candidates.error = action.error.message;
      })

      .addCase(getFactoringBatches.pending, (state) => {
        state.batches.status = 'loading';
      })
      .addCase(getFactoringBatches.fulfilled, (state, action) => {
        state.batches.status = 'success';
        Object.assign(state.batches, action.payload);
      })
      .addCase(getFactoringBatches.rejected, (state, action) => {
        state.batches.status = 'failure';
        state.batches.error = action.error.message;
      })

      .addCase(getFactoringBatchById.pending, (state) => {
        state.batch.status = 'loading';
      })
      .addCase(getFactoringBatchById.fulfilled, (state, action) => {
        state.batch.status = 'success';
        state.batch.data = action.payload;
      })
      .addCase(getFactoringBatchById.rejected, (state, action) => {
        state.batch.status = 'failure';
        state.batch.error = action.error.message;
      })

      .addCase(markAdvanceReceived.fulfilled, (state, action) => {
        state.batch.data = action.payload;
      })

      .addCase(getOverdueSettlements.pending, (state) => {
        state.overdue.status = 'loading';
      })
      .addCase(getOverdueSettlements.fulfilled, (state, action) => {
        state.overdue.status = 'success';
        Object.assign(state.overdue, action.payload);
      })
      .addCase(getOverdueSettlements.rejected, (state, action) => {
        state.overdue.status = 'failure';
        state.overdue.error = action.error.message;
      })

      .addCase(getDebtors.pending, (state) => {
        state.debtors.status = 'loading';
      })
      .addCase(getDebtors.fulfilled, (state, action) => {
        state.debtors.status = 'success';
        Object.assign(state.debtors, action.payload);
      })
      .addCase(getDebtors.rejected, (state, action) => {
        state.debtors.status = 'failure';
        state.debtors.error = action.error.message;
      })

      .addCase(getFactorableSummary.pending, (state) => {
        state.factorableSummary.status = 'loading';
      })
      .addCase(getFactorableSummary.fulfilled, (state, action) => {
        state.factorableSummary.status = 'success';
        state.factorableSummary.data = action.payload;
      })
      .addCase(getFactorableSummary.rejected, (state, action) => {
        state.factorableSummary.status = 'failure';
        state.factorableSummary.error = action.error.message;
      });
  },
});

export default chequeFactoring.reducer;

export const getFactoringCandidates = createAsyncThunk(
  'chequeFactoring/getFactoringCandidates',
  async (
    params: {
      page: number;
      limit: number;
      generalSearch?: string | null;
      sortBy?: string | null;
      sort?: 'asc' | 'desc' | null;
      includeAll?: boolean;
    } = {
      page: 1,
      limit: 10,
      generalSearch: null,
      sort: 'asc',
      sortBy: 'chequeDate',
    }
  ) => {
    const { page, limit, generalSearch, sortBy, sort, includeAll } = params;

    try {
      const response = await axios.get(
        `cheque-factoring/candidates?page=${page + 1}&limit=${limit}&generalSearch=${
          generalSearch ?? ''
        }&sort=${sort ?? ''}&sortBy=${sortBy ?? ''}${includeAll ? '&includeAll=true' : ''}`
      );

      return response.data.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }
);

export const getFactorableSummary = createAsyncThunk(
  'chequeFactoring/getFactorableSummary',
  async () => {
    const response = await axios.get('cheque-factoring/candidates/factorable-summary');
    return response.data.data;
  }
);

export interface TCreateFactoringBatch {
  batchDate: Date;
  chequeIds: string[];
}

export const createFactoringBatch = createAsyncThunk(
  'chequeFactoring/createFactoringBatch',
  async (params: { newBatch: TCreateFactoringBatch }) => {
    const { newBatch } = params;
    const response = await axios.post(`cheque-factoring/batches`, newBatch);
    return response.data.data;
  }
);

export const getFactoringBatches = createAsyncThunk(
  'chequeFactoring/getFactoringBatches',
  async (
    params: {
      page: number;
      limit: number;
      status?: string | null;
      startDate?: Date | null;
      endDate?: Date | null;
      sortBy?: string | null;
      sort?: 'asc' | 'desc' | null;
    } = {
      page: 1,
      limit: 10,
      status: null,
      startDate: null,
      endDate: null,
      sort: 'desc',
      sortBy: 'createdAt',
    }
  ) => {
    const { page, limit, status, startDate, endDate, sortBy, sort } = params;

    try {
      const response = await axios.get(
        `cheque-factoring/batches?page=${page + 1}&limit=${limit}&status=${
          status ?? ''
        }&sort=${sort ?? ''}&sortBy=${sortBy ?? ''}&startDate=${fAPIDate(
          startDate
        )}&endDate=${fAPIDate(endDate)}`
      );

      return response.data.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }
);

export const getFactoringBatchById = createAsyncThunk(
  'chequeFactoring/getFactoringBatchById',
  async (params: { batchId: string }) => {
    const { batchId } = params;
    const response = await axios.get(`cheque-factoring/batches/${batchId}`);
    return response.data.data;
  }
);

export const deleteFactoringBatch = createAsyncThunk(
  'chequeFactoring/deleteFactoringBatch',
  async (params: { batchId: string }) => {
    const { batchId } = params;
    const response = await axios.delete(`cheque-factoring/batches/${batchId}`);
    return response.data.data;
  }
);

export const markAdvanceReceived = createAsyncThunk(
  'chequeFactoring/markAdvanceReceived',
  async (params: { batchId: string; advanceReceivedDate: Date }) => {
    const { batchId, advanceReceivedDate } = params;
    const response = await axios.patch(`cheque-factoring/batches/${batchId}/advance-received`, {
      advanceReceivedDate,
    });
    return response.data.data;
  }
);

export const markChequeCleared = createAsyncThunk(
  'chequeFactoring/markChequeCleared',
  async (params: { chequeId: string; clearedDate: Date }) => {
    const { chequeId, clearedDate } = params;
    const response = await axios.patch(`cheque-factoring/cheques/${chequeId}/cleared`, {
      clearedDate,
    });
    return response.data.data;
  }
);

export const markChequeReturned = createAsyncThunk(
  'chequeFactoring/markChequeReturned',
  async (params: { chequeId: string; returnedDate: Date }) => {
    const { chequeId, returnedDate } = params;
    const response = await axios.patch(`cheque-factoring/cheques/${chequeId}/returned`, {
      returnedDate,
    });
    return response.data.data;
  }
);

export const markChequeSettled = createAsyncThunk(
  'chequeFactoring/markChequeSettled',
  async (params: { chequeId: string; settledDate: Date }) => {
    const { chequeId, settledDate } = params;
    const response = await axios.patch(`cheque-factoring/cheques/${chequeId}/settled`, {
      settledDate,
    });
    return response.data.data;
  }
);

export const getOverdueSettlements = createAsyncThunk(
  'chequeFactoring/getOverdueSettlements',
  async (
    params: {
      page: number;
      limit: number;
      sortBy?: string | null;
      sort?: 'asc' | 'desc' | null;
    } = {
      page: 1,
      limit: 10,
      sort: 'asc',
      sortBy: 'factoringSettlementDueAt',
    }
  ) => {
    const { page, limit, sortBy, sort } = params;

    try {
      const response = await axios.get(
        `cheque-factoring/overdue-settlements?page=${page + 1}&limit=${limit}&sort=${
          sort ?? ''
        }&sortBy=${sortBy ?? ''}`
      );

      return response.data.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }
);

export const getDebtors = createAsyncThunk(
  'chequeFactoring/getDebtors',
  async (
    params: {
      page: number;
      limit: number;
      generalSearch?: string | null;
      sortBy?: string | null;
      sort?: 'asc' | 'desc' | null;
    } = {
      page: 1,
      limit: 10,
      generalSearch: null,
      sort: 'desc',
      sortBy: 'createdAt',
    }
  ) => {
    const { page, limit, generalSearch, sortBy, sort } = params;

    try {
      const response = await axios.get(
        `cheque-factoring/debtors?page=${page + 1}&limit=${limit}&generalSearch=${
          generalSearch ?? ''
        }&sort=${sort ?? ''}&sortBy=${sortBy ?? ''}`
      );

      return response.data.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }
);
